import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
} from '@/components/ui/sheet';
import { SheetCloseSticky } from './sheet-close-variants/sticky';
import { keepPreviousData, skipToken, useQuery } from '@tanstack/react-query';
import { useLocation, useNavigate } from '@tanstack/react-router';
import { CMSText } from '@/components/cms/cms-text';
import { match } from 'ts-pattern';
import { FullScreenLoadingSpinner } from '@/components/full-screen-loading-spinner';
import { cn } from '@/lib/utils';
import { PAGE_PARTIAL_SLUG } from './constants';
import { cmsQueryKeys } from '@/services/cms/cmsQueryKeys';
import { getPartialBySlug } from '@/services/cms/getPartialBySlug';
import { CMSColumns } from '../cms/cms-columns';
import { CMSPlaceholder } from '../cms/cms-placeholder';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';

const PartialSheet = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const drawer = location.search.drawer;

  const isOpen = !!drawer && PAGE_PARTIAL_SLUG.includes(drawer);

  const { data, isFetching, isError } = useQuery({
    queryKey: [...cmsQueryKeys.getPartialBySlug, drawer],
    queryFn: isOpen ? () => getPartialBySlug({ slug: drawer }) : skipToken,
    enabled: isOpen,
    staleTime: Infinity,
    placeholderData: keepPreviousData,
  });

  if (isError) {
    return null;
  }

  const onClose = () => {
    navigate({
      to: location.pathname,
      search: (prev) => ({ ...prev, drawer: undefined }),
    });
  };

  return (
    <Sheet open={isOpen}>
      <SheetContent className="w-full sm:max-w-none md:max-w-[368px] lg:max-w-[510px] xl:max-w-[510px]">
        <VisuallyHidden.Root>
          <SheetTitle>CMS Sheet</SheetTitle>
          <SheetDescription>CMS Partial Page Sheet</SheetDescription>
        </VisuallyHidden.Root>

        <SheetCloseSticky
          header={!isFetching ? data?.title : ''}
          onClose={onClose}
        />
        <div className="w-full pt-4 px-5 md:p-6 lg:px-8">
          {isFetching ? (
            <FullScreenLoadingSpinner />
          ) : (
            <>
              {data?.content.map((content, index) =>
                match(content)
                  .with({ type: 'text' } as const, (textData) => (
                    <CMSText
                      key={index}
                      textData={textData}
                      className={cn(
                        'cms-render [&_ul_li::before]:bg-grey-900',

                        //Add color for h1 top level CMSText
                        '[&_h1]:text-grey-900',
                        //Add color for h2 top level CMSText
                        '[&_h2]:text-grey-900',

                        //Add color for h3 top level CMSText
                        '[&_h3]:text-grey-900',

                        // Add bold for h2
                        '[&_h2]:font-nunito700',
                        //Add text size for h2
                        `[&_h2]:text-lg 
                         md:[&_h2]:text-2xl 
                         lg:[&_h2]:text-3xl`,

                        //Add margin top to h1 tag itself
                        '[&_h1]:mt-8 md:[&_h1]:mt-12 lg:[&_h1]:mt-20',

                        //Add margin top to h2 tag itself
                        '[&_h2]:mt-8 md:[&_h2]:mt-12 lg:[&_h2]:mt-20',

                        //Add margin top to h3 tag itself
                        '[&_h3]:mt-6 md:[&_h3]:mt-8',

                        // Add margin bottom if CMSText is first child then apply it to first child paragraph
                        '[&:first-child_p:first-child]:mb-4',

                        '[&_p]:text-sm lg:[&_p]:text-base [&_p]:text-grey-900 [&_p]:mb-2',

                        // Add style for tag that has a tag
                        '[&_p_a]:text-sm lg:[&_p_a]:text-base [&_p_a]:cursor-pointer [&_p_a]:no-underline [&_p_a]:text-grey-900',

                        // Add style for bold tag
                        '[&_p_strong]:text-grey-900',

                        // Add style for small tag inside p tag
                        '[&__p_small]:text-xs md:[&__p_small]:text-xs lg:[&_p_small]:text-xs',

                        // Add style for img inside p tag,
                        '[&_p_img]:my-6',

                        //Add margin y axis for p tag
                        '[&_p]:mb-2 md:[&_p]:mb-4 [&_p:not(:first-child)]:mt-2 md:[&_p:not(:first-child)]:mt-4',

                        // Add style for CTA
                        '[&_[data-component="CMSCTAButton"]_a]:no-underline [&_[data-component="CMSCTAButton"]_a]:text-sm lg:[&_[data-component="CMSCTAButton"]_a]:text-base [&_[data-component="CMSCTAButton"]_a]:text-grey-900 [&_[data-component="CMSCTAButton"]_a]:font-light'
                      )}
                    />
                  ))
                  .with({ type: 'columns' }, (columnsData) => (
                    <CMSColumns
                      className={cn(
                        //Add padding bottom for CMSColumnsStandard if it's the last child
                        '[&[data-component="CMSColumnsStandard"]:last-child]:pb-[50px] lg:[&[data-component="CMSColumnsStandard"]:last-child]:pb-[80px]',

                        //Set CMSTextAdvanced component to w-full for CMSColumnsStandard
                        '[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]]:w-full',

                        //Add text styling on text within InlineTextImage
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText]:text-base 
                         lg:[&[data-component="CMSColumnsStandard"]_[data-component="CMSTextAdvanced"]_[data-component="CMSInlineTextImage"]_.richText]:text-lg`,

                        //Add text size for h2 inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2]:text-lg 
                         md:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2]:text-2xl 
                         lg:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2]:text-3xl`,

                        // Add text size for h3 inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3]:text-base 
                         md:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3]:text-lg 
                         lg:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3]:text-xl`,

                        //Add text color for h2 and h3 inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2]:text-grey-900 
                        [&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3]:text-grey-900`,

                        //Add text deep teal for p tag inside CMSColumnsStandard
                        '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:text-grey-900',

                        // Add margin bottom for p tag inside CMSColumnsStandard
                        '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:mb-4',

                        // Add paragraph text sizes inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:text-sm 
                         md:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:text-base 
                         lg:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:text-lg`,

                        // Add margin bottom for p tag inside CMSColumnsStandard
                        '[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:mb-4',

                        // Add margin top to h1 tag itself if it is not first child inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h1:not(:first-child)]:mt-8
                         md:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h1:not(:first-child)]:mt-12 
                         lg:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h1:not(:first-child)]:mt-20`,

                        // Add margin top to h2 tag itself if it is not first child inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2:not(:first-child)]:mt-8 
                         md:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2:not(:first-child)]:mt-12 
                         lg:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h2:not(:first-child)]:mt-20`,

                        // Add margin top to h3 tag itself if it is not first child inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3:not(:first-child)]:mt-6 
                         md:[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_h3:not(:first-child)]:mt-8`,

                        //Add margin top for CMSColumnsStandard inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]]:mt-6 
                         md:[&[data-component="CMSColumnsStandard"]]:mt-8`,

                        //Add margin top for CMSColumnsStandard inside CMSColumnsStandard
                        `[&[data-component="CMSColumnsStandard"]]:mb-6 
                         md:[&[data-component="CMSColumnsStandard"]]:mb-8 
                         lg:[&[data-component="CMSColumnsStandard"]]:mb-16`,

                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSText"]_p]:text-grey-900`,

                        `[&[data-component="CMSColumnsStandard"]_[data-component="CMSInlineTextImage"]_.richText]:text-grey-900`
                      )}
                      key={columnsData.id}
                      columnsData={columnsData}
                    />
                  ))
                  .with({ type: 'placeholder' } as const, (data) => (
                    <CMSPlaceholder
                      className={cn(
                        // Set margin top and margin bottom for CMSAppLinks
                        '[&_[data-component="CMSAppLinks"]]:mt-4 [&_[data-component="CMSAppLinks"]]:mb-8',

                        // Add gap for CMSAppLinks
                        '[&_[data-component="CMSAppLinks"]]:gap-[6px]',

                        // Set max width for CMSAppLinks
                        '[&_[data-component="CMSAppLinks"]_img]:max-w-[125px]'
                      )}
                      key={index}
                      placeholderData={data}
                    />
                  ))

                  .otherwise(() => (
                    <div key={index}>Unhandled CMSComponent</div>
                  ))
              )}
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
export { PartialSheet };
